import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Empty, Tag, List } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { Risk, RiskMatrix } from '../types/risk';
import riskService from '../api/riskService';

interface RiskMatrixProps {
  projectId?: number;
}

const RiskMatrixComponent: React.FC<RiskMatrixProps> = ({ projectId }) => {
  const [matrixData, setMatrixData] = useState<RiskMatrix | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchRiskMatrix = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await riskService.getRiskMatrix(projectId);
      setMatrixData(data);
    } catch (error) {
      console.error('Failed to fetch risk matrix:', error);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchRiskMatrix();
  }, [fetchRiskMatrix]);

  const getCellColor = (impact: string, probability: string) => {
    if (impact === 'high' && probability === 'high') return '#ff4d4f';
    if (impact === 'high' && probability === 'medium') return '#ff7a45';
    if (impact === 'medium' && probability === 'high') return '#ff7a45';
    if (impact === 'medium' && probability === 'medium') return '#ffa940';
    if (impact === 'low' && probability === 'high') return '#ffa940';
    if (impact === 'high' && probability === 'low') return '#ffa940';
    if (impact === 'medium' && probability === 'low') return '#ffec3d';
    if (impact === 'low' && probability === 'medium') return '#ffec3d';
    return '#bae637';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Identified': return 'blue';
      case 'Mitigated': return 'green';
      case 'Occurred': return 'red';
      case 'Closed': return 'gray';
      default: return 'default';
    }
  };

  const impactLevels = ['high', 'medium', 'low'];
  const probabilityLevels = ['high', 'medium', 'low'];

  if (!matrixData) return <Empty description="No risk data available" />;

  return (
    <Card title="Risk Matrix" loading={loading}>
      <Row gutter={[16, 16]}>
        <Col span={3} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ transform: 'rotate(-90deg)', whiteSpace: 'nowrap' }}>
            Probability →
          </div>
        </Col>
        
        {probabilityLevels.map(prob => (
          <Col span={7} key={prob}>
            <div style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 8 }}>
              {prob.charAt(0).toUpperCase() + prob.slice(1)} Probability
            </div>
          </Col>
        ))}
      </Row>

      {impactLevels.map(impact => (
        <Row gutter={[16, 16]} key={impact} style={{ marginBottom: 16 }}>
          <Col span={3} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ fontWeight: 'bold' }}>
              {impact.charAt(0).toUpperCase() + impact.slice(1)} Impact
            </div>
          </Col>

          {probabilityLevels.map(probability => {
            const risks = matrixData[`${impact}_impact` as keyof RiskMatrix]?.[
              `${probability}_prob` as keyof typeof matrixData.high_impact
            ] as Risk[] || [];
            
            const cellColor = getCellColor(impact, probability);

            return (
              <Col span={7} key={probability}>
                <div
                  style={{
                    backgroundColor: cellColor,
                    minHeight: 150,
                    padding: 12,
                    borderRadius: 6,
                    border: '1px solid #d9d9d9'
                  }}
                >
                  <List
                    size="small"
                    dataSource={risks}
                    renderItem={(risk: Risk) => (
                      <List.Item>
                        <div style={{ width: '100%' }}>
                          <div style={{ fontWeight: 'bold', fontSize: '12px' }}>
                            {risk.name}
                          </div>
                          <Tag color={getStatusColor(risk.status)} style={{ fontSize: '11px' }}>
                            {risk.status}
                          </Tag>
                        </div>
                      </List.Item>
                    )}
                    locale={{ emptyText: 'No risks' }}
                  />
                </div>
              </Col>
            );
          })}
        </Row>
      ))}

      <div style={{ marginTop: 16, fontSize: '12px', color: '#666' }}>
        <WarningOutlined /> Risks are positioned based on their impact and probability levels
      </div>
    </Card>
  );
};

export default RiskMatrixComponent;